import type { HelpScreenContext, HelpScreenKey, HelpScreenRoute } from "@/interfaces/helpScreens";

/**
 * Hand-kept copy of HELP_CONTEXT_KEYS in apps/gfgf-api/src/constants/helpCenter.ts and the
 * Studio's contextKeys list in apps/gfgf-studio/schemas/helpArticle.ts. The workspaces share no
 * package (see interfaces/help.ts's header), so edit all three together or a screen and its
 * articles drift apart silently.
 */
export const HELP_SCREEN_KEYS = {
  DASHBOARD: "dashboard",
  CAMPAIGNS: "campaigns",
  THANKYOU: "thankyou",
  REPORTS: "reports",
  IMPACT: "impact",
  SETTINGS: "settings",
  WALLET: "wallet",
  BUILDER_COLLECTION: "builder.collection",
  BUILDER_RECIPIENTS: "builder.recipients",
  BUILDER_DESIGN: "builder.design",
  BUILDER_REVIEW: "builder.review",
} as const;

/**
 * The "Suggested help" set that GFG staff-only pages and unmatched routes get.
 */
export const HELP_SUGGESTED_SCREEN_KEYS: readonly HelpScreenKey[] = [HELP_SCREEN_KEYS.DASHBOARD];

/** What any route not in HELP_SCREEN_ROUTES resolves to. */
export const HELP_SCREEN_FALLBACK: HelpScreenContext = {
  keys: HELP_SUGGESTED_SCREEN_KEYS,
  label: null,
};

/**
 * The route → help-screen table, signed off by Judd on 2026-09-23 (GFG-682).
 *
 * Rows are matched in order with react-router's matchPath; the first match wins. A row may carry
 * several keys; the panel requests all of them and the API merges the results (GFG-768, see
 * helpScreenRequestKeys in utils/help-context-helper.ts). Every row has at least one key.
 * Accounting and Finance are GFG staff-only and get the Suggested set (Chris, 2026-09-24).
 * Labels are UI copy that Judd may edit later.
 */
export const HELP_SCREEN_ROUTES: readonly HelpScreenRoute[] = [
  { paths: ["/dashboard"], keys: [HELP_SCREEN_KEYS.DASHBOARD], label: "Dashboard" },
  { paths: ["/campaigns"], keys: [HELP_SCREEN_KEYS.CAMPAIGNS], label: "My Campaigns" },
  { paths: ["/thanks-report"], keys: [HELP_SCREEN_KEYS.THANKYOU], label: "Thank You Notes" },
  { paths: ["/impact"], keys: [HELP_SCREEN_KEYS.IMPACT], label: "Impact" },
  {
    paths: ["/account", "/profile", "/settings"],
    keys: [HELP_SCREEN_KEYS.SETTINGS, HELP_SCREEN_KEYS.WALLET],
    label: "Account",
  },
  {
    paths: ["/collections", "/collections/tier/:budget", "/collections/:id"],
    keys: [HELP_SCREEN_KEYS.BUILDER_COLLECTION, HELP_SCREEN_KEYS.DASHBOARD],
    label: "Collections",
  },
  { paths: ["/contacts"], keys: [HELP_SCREEN_KEYS.BUILDER_RECIPIENTS], label: "Contacts" },
  { paths: ["/users"], keys: [HELP_SCREEN_KEYS.SETTINGS], label: "Users" },
  {
    // tier/:tier pre-selects a budget, then redirects to Step 1.
    paths: ["/campaign/create", "/campaign/create/collection", "/campaign/create/tier/:tier"],
    keys: [HELP_SCREEN_KEYS.BUILDER_COLLECTION],
    label: "Builder: Collection",
  },
  {
    paths: ["/campaign/create/recipients"],
    keys: [HELP_SCREEN_KEYS.BUILDER_RECIPIENTS],
    label: "Builder: Recipients",
  },
  {
    paths: ["/campaign/create/design"],
    keys: [HELP_SCREEN_KEYS.BUILDER_DESIGN],
    label: "Builder: Design",
  },
  {
    paths: ["/campaign/create/review"],
    keys: [HELP_SCREEN_KEYS.BUILDER_REVIEW],
    label: "Builder: Review",
  },
  {
    paths: ["/campaign/create/reminders"],
    keys: [HELP_SCREEN_KEYS.BUILDER_RECIPIENTS, HELP_SCREEN_KEYS.CAMPAIGNS],
    label: "Reminders",
  },
  {
    paths: [
      "/gfg-dashboard",
      "/users-orgs",
      "/audit-log",
      "/campaign-tester",
      "/actions",
      "/accounting",
      "/finance",
    ],
    keys: HELP_SUGGESTED_SCREEN_KEYS,
    label: null,
  },
];
